import type {
  AddReleaseTesterRequest,
  Announcement,
  AppVersion,
  AssetUploadRequestSchema,
  AuditLog,
  AuthTokensResponse,
  BrandAssetKind,
  BulkCreateHostsRequest,
  CreateFeaturedSectionRequest,
  Branding,
  CreateAnnouncementRequestSchema,
  CreateHostRequest,
  CreateTenantRequest,
  CreateTenantUserRequest,
  Device,
  ErrorCode,
  FeaturedSection,
  GlobalSettings,
  GrowthReport,
  Host,
  HostCheckLink,
  HostUploadLink,
  HostProbeRequest,
  HostProbeResult,
  HostTestAllResult,
  HostTestResult,
  HostMonitor,
  HostProvider,
  HostUsage,
  LibraryProvider,
  Page,
  PilotState,
  Plan,
  ProviderCategories,
  PlatformOverview,
  Principal,
  PromotePilotRequestSchema,
  PublishReleaseRequestSchema,
  RecoveryCodes,
  ReleaseSource,
  ReleaseTester,
  SendLibraryHostsRequest,
  SendLibraryHostsResult,
  TenantSettings,
  TenantDetail,
  TenantSummary,
  TenantUsage,
  TenantUser,
  TvReleaseState,
  TwoFactorChallenge,
  TwoFactorSetup,
  UpdateBrandingRequest,
  UpdateFeaturedSectionRequest,
  UpdateGlobalSettingsRequestSchema,
  UpdateHostRequest,
  UpdateTenantRequest,
  UpdateTenantSettingsRequest,
  UpdateTenantUserRequest,
  UpsertAppVersionRequest,
  UpsertPlanRequest,
  VerifiedLoginResponse,
} from '@iptv/shared-types';
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
  }
}

export interface MerchantOverview {
  id: string;
  name: string;
  status: string;
  merchantCode: string | null;
  expiresAt: string | null;
  plan: { code: string; name: string; capabilities: Plan['capabilities'] };
  limits: { maxDevices: number; maxMerchantUsers: number; maxHosts: number };
  /** devicesInUse: devices holding a session, which is what limits.maxDevices counts. */
  usage: { devicesInUse: number; hosts: number };
}

type Query = Record<string, string | number | boolean | undefined | null>;

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]!) : null;
}

/**
 * Browser client for the panels. The access token lives in memory only; the refresh token is an
 * HttpOnly cookie, rotated through /v1/auth/refresh with the double-submit CSRF header.
 */
export class PanelApi {
  private accessToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  onSessionLost: () => void = () => undefined;

  constructor(private readonly baseUrl: string) {}

  /** An absolute address on the API, for links shown to people (download links). */
  urlFor(path: string): string {
    return new URL(path, this.baseUrl).toString();
  }

  get isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  async request<T>(
    method: string,
    path: string,
    opts: { body?: unknown; query?: Query; retry?: boolean } = {},
  ): Promise<T> {
    const url = new URL(path, this.baseUrl);
    for (const [k, v] of Object.entries(opts.query ?? {}))
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(this.accessToken ? { authorization: `Bearer ${this.accessToken}` } : {}),
        },
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'Network error');
    }
    if (res.status === 401 && opts.retry !== false && this.accessToken) {
      if (await this.refresh()) return this.request<T>(method, path, { ...opts, retry: false });
      this.onSessionLost();
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      // A proxy error page (502/504 HTML) or a truncated body: report a clean error code instead.
      throw new ApiError(
        res.status,
        res.status >= 500 || res.ok ? 'INTERNAL' : 'VALIDATION_FAILED',
        res.statusText,
      );
    }
    if (!res.ok) {
      const err = (
        json as {
          error?: {
            code: ErrorCode;
            message: string;
            details?: { path: string; message: string }[];
          };
        }
      )?.error;
      throw new ApiError(
        res.status,
        err?.code ?? 'INTERNAL',
        err?.message ?? res.statusText,
        err?.details,
      );
    }
    return json as T;
  }

  // ---------------------------------------------------------------- auth

  /** The signed-in user, or a two-step challenge to finish with `verifyLogin`. */
  async login(email: string, password: string): Promise<Principal | TwoFactorChallenge> {
    const res = await this.request<AuthTokensResponse | TwoFactorChallenge>(
      'POST',
      '/v1/auth/login',
      { body: { email, password }, retry: false },
    );
    if ('challengeToken' in res) return res;
    this.accessToken = res.accessToken;
    return res.user;
  }

  /** A new authenticator secret for an account that must turn 2FA on while signing in. */
  loginSetup(challengeToken: string) {
    return this.request<TwoFactorSetup>('POST', '/v1/auth/login/setup', {
      body: { challengeToken },
      retry: false,
    });
  }

  /** Recovery codes come back only when this sign-in turned 2FA on. */
  async verifyLogin(
    challengeToken: string,
    code: string,
  ): Promise<{ user: Principal; recoveryCodes?: string[] }> {
    const res = await this.request<VerifiedLoginResponse>('POST', '/v1/auth/login/verify', {
      body: { challengeToken, code },
      retry: false,
    });
    this.accessToken = res.accessToken;
    return { user: res.user, recoveryCodes: res.recoveryCodes };
  }

  /** Restores a session from the refresh cookie (page reload). */
  async restore(): Promise<Principal | null> {
    if (!(await this.refresh())) return null;
    return this.me();
  }

  me() {
    return this.request<Principal>('GET', '/v1/auth/me');
  }

  refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      const csrf = readCookie('iptv_csrf');
      if (!csrf) return false;
      try {
        const res = await fetch(new URL('/v1/auth/refresh', this.baseUrl), {
          method: 'POST',
          credentials: 'include',
          headers: { 'x-csrf-token': csrf },
        });
        if (!res.ok) {
          this.accessToken = null;
          return false;
        }
        this.accessToken = ((await res.json()) as AuthTokensResponse).accessToken;
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  async logout(): Promise<void> {
    const csrf = readCookie('iptv_csrf') ?? '';
    await fetch(new URL('/v1/auth/logout', this.baseUrl), {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': csrf },
    }).catch(() => undefined);
    this.accessToken = null;
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.request('POST', '/v1/auth/change-password', {
      body: { currentPassword, newPassword },
    });
  }

  /** The signed-in user's own two-step verification. */
  twoFactor = {
    setup: () => this.request<TwoFactorSetup>('POST', '/v1/auth/two-factor/setup'),
    enable: (code: string) =>
      this.request<RecoveryCodes>('POST', '/v1/auth/two-factor/enable', { body: { code } }),
    disable: (password: string, code: string) =>
      this.request('POST', '/v1/auth/two-factor/disable', { body: { password, code } }),
    recoveryCodes: (code: string) =>
      this.request<RecoveryCodes>('POST', '/v1/auth/two-factor/recovery-codes', {
        body: { code },
      }),
  };

  // ---------------------------------------------------------------- merchant

  merchant = {
    overview: () => this.request<MerchantOverview>('GET', '/v1/merchant/overview'),
    usage: () => this.request<TenantUsage>('GET', '/v1/merchant/usage'),
    announcements: () => this.request<Announcement[]>('GET', '/v1/merchant/announcements'),
    branding: () => this.request<Branding>('GET', '/v1/merchant/branding'),
    updateBranding: (b: UpdateBrandingRequest) =>
      this.request<Branding>('PATCH', '/v1/merchant/branding', { body: b }),
    uploadAsset: async (kind: BrandAssetKind, file: File): Promise<Branding> => {
      const req: z.input<typeof AssetUploadRequestSchema> = {
        kind,
        contentType: file.type as never,
        sizeBytes: file.size,
      };
      const signed = await this.request<{
        uploadUrl: string;
        headers: Record<string, string>;
        assetKey: string;
      }>('POST', '/v1/merchant/branding/assets/upload-url', { body: req });
      const put = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: signed.headers,
        body: file,
      }).catch(() => {
        throw new ApiError(0, 'NETWORK', 'Upload failed');
      });
      if (!put.ok) throw new ApiError(put.status, 'INTERNAL', 'Upload failed');
      return this.request<Branding>('POST', '/v1/merchant/branding/assets/commit', {
        body: { kind, assetKey: signed.assetKey },
      });
    },
    removeAsset: (kind: BrandAssetKind) =>
      this.request<Branding>('DELETE', `/v1/merchant/branding/assets/${kind}`),
    settings: () => this.request<TenantSettings>('GET', '/v1/merchant/settings'),
    updateSettings: (s: UpdateTenantSettingsRequest) =>
      this.request<TenantSettings>('PATCH', '/v1/merchant/settings', { body: s }),
    featured: () => this.request<FeaturedSection[]>('GET', '/v1/merchant/featured'),
    createFeatured: (req: CreateFeaturedSectionRequest) =>
      this.request<FeaturedSection>('POST', '/v1/merchant/featured', { body: req }),
    updateFeatured: (id: string, req: UpdateFeaturedSectionRequest) =>
      this.request<FeaturedSection>('PATCH', `/v1/merchant/featured/${id}`, { body: req }),
    deleteFeatured: (id: string) => this.request('DELETE', `/v1/merchant/featured/${id}`),
    reorderFeatured: (ids: string[]) =>
      this.request<FeaturedSection[]>('PUT', '/v1/merchant/featured/order', { body: { ids } }),
    /** Uploads a section logo and returns its key, to save with the section. */
    uploadFeaturedLogo: async (file: File): Promise<string> => {
      const signed = await this.request<{
        uploadUrl: string;
        headers: Record<string, string>;
        assetKey: string;
      }>('POST', '/v1/merchant/featured/logo-upload', {
        body: { contentType: file.type, sizeBytes: file.size },
      });
      const put = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: signed.headers,
        body: file,
      }).catch(() => {
        throw new ApiError(0, 'NETWORK', 'Upload failed');
      });
      if (!put.ok) throw new ApiError(put.status, 'INTERNAL', 'Upload failed');
      return signed.assetKey;
    },
    /** Reads the provider's categories with a subscriber login used once; it is never stored. */
    providerCategories: (account: { username: string; password: string }) =>
      this.request<ProviderCategories>('POST', '/v1/merchant/featured/provider-categories', {
        body: { account },
      }),
    hosts: () => this.request<Host[]>('GET', '/v1/merchant/hosts'),
    hostUsage: () => this.request<HostUsage[]>('GET', '/v1/merchant/hosts/usage'),
    hostMonitor: () => this.request<HostMonitor>('GET', '/v1/merchant/hosts/monitor'),
    hostCheckLink: () => this.request<HostCheckLink>('GET', '/v1/merchant/hosts/check-link'),
    createHostCheckLink: () => this.request<HostCheckLink>('POST', '/v1/merchant/hosts/check-link'),
    deleteHostCheckLink: () => this.request('DELETE', '/v1/merchant/hosts/check-link'),
    setHostMonitor: (account: { username: string; password: string }) =>
      this.request<HostMonitor>('PUT', '/v1/merchant/hosts/monitor', { body: account }),
    clearHostMonitor: () => this.request('DELETE', '/v1/merchant/hosts/monitor'),
    createHostProvider: (name: string) =>
      this.request<HostProvider>('POST', '/v1/merchant/hosts/providers', { body: { name } }),
    renameHostProvider: (id: string, name: string) =>
      this.request<HostProvider>('PATCH', `/v1/merchant/hosts/providers/${id}`, {
        body: { name },
      }),
    deleteHostProvider: (id: string) =>
      this.request('DELETE', `/v1/merchant/hosts/providers/${id}`),
    setProviderHosts: (id: string, hostIds: string[]) =>
      this.request<HostMonitor>('PUT', `/v1/merchant/hosts/providers/${id}/hosts`, {
        body: { hostIds },
      }),
    setHostProviderMonitor: (id: string, account: { username: string; password: string }) =>
      this.request<HostMonitor>('PUT', `/v1/merchant/hosts/providers/${id}/monitor`, {
        body: account,
      }),
    clearHostProviderMonitor: (id: string) =>
      this.request('DELETE', `/v1/merchant/hosts/providers/${id}/monitor`),
    createHost: (h: CreateHostRequest) =>
      this.request<Host>('POST', '/v1/merchant/hosts', { body: h }),
    updateHost: (id: string, h: UpdateHostRequest) =>
      this.request<Host>('PATCH', `/v1/merchant/hosts/${id}`, { body: h }),
    deleteHost: (id: string) => this.request('DELETE', `/v1/merchant/hosts/${id}`),
    /** [account] is a subscriber login used for this test only; it is never stored. */
    testHost: (id: string, account?: { username: string; password: string }) =>
      this.request<HostTestResult>('POST', `/v1/merchant/hosts/${id}/test`, {
        body: account ? { account } : {},
      }),
    /** Tests pasted links before they are added; nothing is stored, the account included. */
    probeHosts: (req: HostProbeRequest) =>
      this.request<HostProbeResult[]>('POST', '/v1/merchant/hosts/probe', { body: req }),
    createHosts: (req: BulkCreateHostsRequest) =>
      this.request<Host[]>('POST', '/v1/merchant/hosts/bulk', { body: req }),
    testAllHosts: (account?: { username: string; password: string }) =>
      this.request<HostTestAllResult[]>('POST', '/v1/merchant/hosts/test-all', {
        body: account ? { account } : {},
      }),
    deleteHosts: (ids: string[]) =>
      this.request('POST', '/v1/merchant/hosts/bulk-delete', { body: { ids } }),
    hostHealth: (id: string) =>
      this.request<
        { checkedAt: string; ok: boolean; latencyMs: number | null; errorCode: string | null }[]
      >('GET', `/v1/merchant/hosts/${id}/health`),
    devices: (q: Query) => this.request<Page<Device>>('GET', '/v1/merchant/devices', { query: q }),
    logoutDevice: (id: string) => this.request('POST', `/v1/merchant/devices/${id}/logout`),
    blockDevice: (id: string) => this.request('POST', `/v1/merchant/devices/${id}/block`),
    unblockDevice: (id: string) => this.request('POST', `/v1/merchant/devices/${id}/unblock`),
    users: () => this.request<TenantUser[]>('GET', '/v1/merchant/users'),
    createUser: (u: CreateTenantUserRequest) =>
      this.request<TenantUser>('POST', '/v1/merchant/users', { body: u }),
    updateUser: (id: string, u: UpdateTenantUserRequest) =>
      this.request<TenantUser>('PATCH', `/v1/merchant/users/${id}`, { body: u }),
    deleteUser: (id: string) => this.request('DELETE', `/v1/merchant/users/${id}`),
    resetUserTwoFactor: (id: string) =>
      this.request<TenantUser>('POST', `/v1/merchant/users/${id}/reset-two-factor`),
    audit: (q: Query) =>
      this.request<Page<AuditLog>>('GET', '/v1/merchant/audit-logs', { query: q }),
  };

  // ---------------------------------------------------------------- admin

  admin = {
    overview: () => this.request<PlatformOverview>('GET', '/v1/admin/overview'),
    growth: () => this.request<GrowthReport>('GET', '/v1/admin/growth'),
    plans: () => this.request<Plan[]>('GET', '/v1/admin/plans'),
    createPlan: (p: UpsertPlanRequest) =>
      this.request<Plan>('POST', '/v1/admin/plans', { body: p }),
    updatePlan: (id: string, p: UpsertPlanRequest) =>
      this.request<Plan>('PUT', `/v1/admin/plans/${id}`, { body: p }),
    tenants: (q: Query) =>
      this.request<Page<TenantSummary>>('GET', '/v1/admin/tenants', { query: q }),
    tenant: (id: string) => this.request<TenantDetail>('GET', `/v1/admin/tenants/${id}`),
    createTenant: (t: CreateTenantRequest) =>
      this.request<TenantSummary>('POST', '/v1/admin/tenants', { body: t }),
    updateTenant: (id: string, t: UpdateTenantRequest) =>
      this.request<TenantSummary>('PATCH', `/v1/admin/tenants/${id}`, { body: t }),
    setTenantStatus: (id: string, status: 'ACTIVE' | 'SUSPENDED' | 'DISABLED', reason?: string) =>
      this.request<TenantSummary>('POST', `/v1/admin/tenants/${id}/status`, {
        body: { status, reason },
      }),
    archiveTenant: (id: string) => this.request('DELETE', `/v1/admin/tenants/${id}`),
    tenantUsers: (id: string) => this.request<TenantUser[]>('GET', `/v1/admin/tenants/${id}/users`),
    resetUserTwoFactor: (tenantId: string, userId: string) =>
      this.request<TenantUser>(
        'POST',
        `/v1/admin/tenants/${tenantId}/users/${userId}/reset-two-factor`,
      ),
    resetCode: (id: string, code?: string) =>
      this.request<{ code: string }>('POST', `/v1/admin/tenants/${id}/merchant-code`, {
        body: code ? { code } : {},
      }),
    setFeatures: (id: string, overrides: Record<string, boolean | null>) =>
      this.request<{ overrides: Record<string, boolean> }>(
        'PUT',
        `/v1/admin/tenants/${id}/features`,
        { body: { overrides } },
      ),
    usage: (id: string) => this.request<TenantUsage>('GET', `/v1/admin/tenants/${id}/usage`),
    settings: () => this.request<GlobalSettings>('GET', '/v1/admin/settings'),
    updateSettings: (s: z.input<typeof UpdateGlobalSettingsRequestSchema>) =>
      this.request<GlobalSettings>('PUT', '/v1/admin/settings', { body: s }),
    announcements: () => this.request<Announcement[]>('GET', '/v1/admin/announcements'),
    createAnnouncement: (a: z.input<typeof CreateAnnouncementRequestSchema>) =>
      this.request<Announcement>('POST', '/v1/admin/announcements', { body: a }),
    deleteAnnouncement: (id: string) => this.request('DELETE', `/v1/admin/announcements/${id}`),
    audit: (q: Query) => this.request<Page<AuditLog>>('GET', '/v1/admin/audit-logs', { query: q }),
    releases: () => this.request<AppVersion[]>('GET', '/v1/admin/releases'),
    upsertRelease: (r: UpsertAppVersionRequest) =>
      this.request<AppVersion>('PUT', '/v1/admin/releases', { body: r }),
    publishRelease: (r: z.input<typeof PublishReleaseRequestSchema>) =>
      this.request<AppVersion[]>('POST', '/v1/admin/releases/publish', { body: r }),
    releaseSource: () => this.request<ReleaseSource>('GET', '/v1/admin/releases/source'),
    releasePilot: () => this.request<PilotState>('GET', '/v1/admin/releases/pilot'),
    promotePilot: (r: z.input<typeof PromotePilotRequestSchema>) =>
      this.request<AppVersion[]>('POST', '/v1/admin/releases/pilot/promote', { body: r }),
    dismissPilot: (version: string) =>
      this.request('POST', '/v1/admin/releases/pilot/dismiss', { body: { version } }),
    releaseTesters: () => this.request<ReleaseTester[]>('GET', '/v1/admin/releases/testers'),
    addReleaseTester: (r: AddReleaseTesterRequest) =>
      this.request('POST', '/v1/admin/releases/testers', { body: r }),
    removeReleaseTester: (id: string) => this.request('DELETE', `/v1/admin/releases/testers/${id}`),
    hostLibrary: () => this.request<LibraryProvider[]>('GET', '/v1/admin/host-library'),
    sendLibraryHosts: (r: SendLibraryHostsRequest) =>
      this.request<SendLibraryHostsResult>('POST', '/v1/admin/host-library/send', { body: r }),
    unlinkLibrary: (id: string) => this.request('DELETE', `/v1/admin/host-library/links/${id}`),
    hostUploadLink: () => this.request<HostUploadLink>('GET', '/v1/admin/host-library/upload-link'),
    createHostUploadLink: (tenantId: string) =>
      this.request<HostUploadLink>('POST', '/v1/admin/host-library/upload-link', {
        body: { tenantId },
      }),
    deleteHostUploadLink: () => this.request('DELETE', '/v1/admin/host-library/upload-link'),
    releaseTv: () => this.request<TvReleaseState>('GET', '/v1/admin/releases/tv'),
    promoteTv: (version: string) =>
      this.request<TvReleaseState>('POST', '/v1/admin/releases/tv/promote', { body: { version } }),
    dismissTv: (version: string) =>
      this.request<TvReleaseState>('POST', '/v1/admin/releases/tv/dismiss', { body: { version } }),
  };
}
